/** A reference id as this screen holds it — a Mongo id already serialised to a string. */
export type ReferenceId = string;

/**
 * A reference id exactly as it can arrive from the API: normally an already-serialised string, but
 * a populated / non-serialised document reaches the client as an object whose `toString()` yields
 * the same id.
 */
export interface SerialisableId {
  toString(): string;
}

/**
 * A supplier or colourway id list off the product payload. The API omits the field on products
 * that have never held one, and legacy rows can carry blank entries, so both are part of the
 * contract.
 */
export type ReferenceIdPayload = Array<ReferenceId | SerialisableId | null | undefined> | null | undefined;

/**
 * The snapshot the duplicate form takes of the source product, alongside the values the form
 * currently holds. `originalSuppliers` / `originalColourLibraries` are captured when the duplicate
 * loads; `suppliers` / `colourLibraries` are what the copy will be saved with.
 */
export interface DuplicateCarryOverInput {
  originalSuppliers?: ReferenceIdPayload;
  suppliers?: ReferenceIdPayload;
  originalColourLibraries?: ReferenceIdPayload;
  colourLibraries?: ReferenceIdPayload;
}

/**
 * Normalises a raw id list from the API into plain string ids, dropping empty entries.
 * @param ids Raw value off the product payload.
 * @returns String ids, in source order.
 */
const toIdList = (ids: ReferenceIdPayload): ReferenceId[] => {
  if (!Array.isArray(ids)) {
    return [];
  }
  return ids.filter((id): id is ReferenceId | SerialisableId => id !== null && id !== undefined && id !== '').map((id) => String(id));
};

/**
 * True when the components of the original product are still reachable from the copy.
 *
 * A status the copy could inherit — colour and quality approvals, care and testing reports,
 * samples — always hangs off a supplier AND a colourway. A copy that keeps neither shares no
 * component with its original, so there is nothing for the component checklist to offer and the
 * checklist is skipped rather than shown with only "Select All".
 *
 * Both sides must survive: keeping a supplier but no colourway (or the reverse) still leaves no
 * status attached to a pair the copy holds.
 *
 * @param product The duplicate form's snapshot plus its current supplier / colourway selection.
 * @returns True when at least one supplier AND one colourway carry over.
 */
export const hasCarryableComponents = (product: DuplicateCarryOverInput | null | undefined): boolean => {
  if (!product) {
    return false;
  }
  const suppliers = new Set(toIdList(product.suppliers));
  const colourways = new Set(toIdList(product.colourLibraries));
  const keepsSupplier = toIdList(product.originalSuppliers).some((supplierId) => suppliers.has(supplierId));
  const keepsColourway = toIdList(product.originalColourLibraries).some((colourwayId) => colourways.has(colourwayId));
  return keepsSupplier && keepsColourway;
};
